/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * Pure helpers for resolving html_css_workspace data bindings into the
 * window.ARCEE_DATA shape for the admin's in-builder preview. Mirrors
 * arcee-client's htmlCssDataBindings.js, but sorts/limits client-side since
 * the session-authenticated records endpoint used here (GetTableRecordsAPIView)
 * doesn't support server-side ORDER BY the way the public API does.
 */

/** Sorts (by binding.sort, e.g. "-created_at") and slices (by binding.limit) a records array. */
export function sortAndLimitRecords(records, binding) {
  let result = records;
  if (binding.sort) {
    const descending = binding.sort.startsWith('-');
    const field = descending ? binding.sort.slice(1) : binding.sort;
    result = [...result].sort((a, b) => {
      const av = a?.[field];
      const bv = b?.[field];
      if (av == null && bv == null) return 0;
      if (av == null) return descending ? 1 : -1;
      if (bv == null) return descending ? -1 : 1;
      if (av < bv) return descending ? 1 : -1;
      if (av > bv) return descending ? -1 : 1;
      return 0;
    });
  }
  if (binding.limit) {
    result = result.slice(0, binding.limit);
  }
  return result;
}

/**
 * Assembles { [div_id]: records[] } from bindings and the react-query results
 * for the subset of bindings that have a table_slug (in the same order).
 * A binding with no table_slug, a failed fetch, or an empty table all
 * resolve to [] — never omitted, never thrown.
 */
export function buildArceeData(bindings, queryResults) {
  const data = {};
  let resultIndex = 0;
  bindings.forEach((binding) => {
    if (!binding.table_slug) {
      data[binding.div_id] = [];
      return;
    }
    const result = queryResults[resultIndex++];
    data[binding.div_id] = result?.isError ? [] : (result?.data || []);
  });
  return data;
}

/** JSON-serializes data for safe embedding inside a <script> tag in an HTML string. */
export function serializeArceeData(data) {
  return JSON.stringify(data).replace(/</g, '\\u003c');
}

/* ── Card buttons and badges ──────────────────────────────────────────────
 *
 * Mirrors arcee-client's htmlCssDataBindings.js, as the rest of this file
 * does. The resolvers close over nothing because they are stringified into
 * the preview iframe by buildCardScript() below — the same trick the client
 * uses, so the builder preview evaluates a rule exactly as the live page will.
 */

/** Indexes card configs by div_id. A config with no div_id is unaddressable, so it is dropped. */
export function buildCardConfig(cardButtons) {
  const out = {};
  (cardButtons || []).forEach((entry) => {
    if (!entry || !entry.div_id) return;
    var buttons = entry.buttons || (entry.button ? [entry.button] : []);
    out[entry.div_id] = { buttons: buttons, badge: entry.badge || null };
  });
  return out;
}

/** Resolves the buttons to show on one row, in order. Unknown operator fails closed. */
export function resolveCardButtons(config, row) {
  var buttons = (config && config.buttons) || [];
  var out = [];

  for (var i = 0; i < buttons.length; i++) {
    var button = buttons[i];
    if (!button || !button.label) continue;

    var show = true;
    var rule = button.visible_when;
    if (rule && rule.field) {
      var value = row ? row[rule.field] : undefined;
      var values = rule.values || [];
      var op = rule.op || 'any';
      if (op === 'any') show = true;
      else if (op === 'in') show = values.indexOf(value) !== -1;
      else if (op === 'eq') show = values[0] === value;
      else if (op === 'neq') show = values[0] !== value;
      else show = false;
    }

    if (show) {
      out.push({ label: button.label, icon: button.icon || '', style: button.style || 'text' });
    }
  }

  return out;
}

/** Resolves the badge for one row, or null when the value has no configured label. */
export function resolveCardBadge(config, row) {
  var badge = config && config.badge;
  if (!badge || !badge.field) return null;

  var value = row ? row[badge.field] : undefined;
  if (value === undefined || value === null || value === '') return null;

  var labels = badge.labels || {};
  var label = labels[value];
  if (!label) return null;

  var colors = badge.colors || {};
  return { label: label, tone: colors[value] || 'muted' };
}

/** The sandbox-side runtime: exposes arceeCardButton/arceeCardBadge to author JS. */
export function buildCardScript() {
  return `;(function(){
  var CARDS = window.ARCEE_CARDS || {};
  var resolveButtons = ${resolveCardButtons.toString()};
  var resolveBadge = ${resolveCardBadge.toString()};
  window.arceeCardButtons = function (divId, row) { return resolveButtons(CARDS[divId], row); };
  window.arceeCardBadge   = function (divId, row) { return resolveBadge(CARDS[divId], row); };
})();`;
}
